/** @jsx jsx */

import { jsx, css } from '@emotion/core';
//
import * as PIXI from 'pixi.js-legacy';
// 'pixi.js';
import { KawaseBlurFilter } from '@pixi/filter-kawase-blur';
import SimplexNoise from 'simplex-noise';
import tinycolor from 'tinycolor2';
import hsl from 'hsl-to-hex';
import { useEffect, useRef } from 'react';
import hexToRGB from 'services/hexToRGB';
import { getWebGLEnabled } from 'services/getWebGLEnabled';

// https://codepen.io/georgedoescode/pen/XWNmvro

export type TAnimatedOrbDefaultValues = {
  primaryOrbColor: string;
  secondaryOrbColor: string;
  tertiaryOrbColor: string;
  orbOpacity: number;
  orbBlurStrength: number;
  orbSpeed: number;
};

export const getAnimatedOrbsDefaultValues = (
  props?: TAnimatedOrbDefaultValues
) => {
  return {
    primaryOrbColor: props?.primaryOrbColor || '#87B5FF' || '#FD7EB8',
    secondaryOrbColor: props?.secondaryOrbColor || '#B14FEC',
    tertiaryOrbColor: props?.tertiaryOrbColor || '#00d9d3',
    orbOpacity: (props?.orbOpacity || 100) * 0.01,
    orbBlurStrength: props?.orbBlurStrength || 30,
    orbSpeed: (props?.orbSpeed || 2) * 0.001,
  };
};

function debounce(func, delay) {
  let timeoutId;

  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      // @ts-ignore
      func.apply(this, args);
    }, delay);
  };
}

function random(min, max) {
  return Math.random() * (max - min) + min;
}

function map(n, start1, end1, start2, end2) {
  return ((n - start1) / (end1 - start1)) * (end2 - start2) + start2;
}

const simplex = new SimplexNoise();

// ColorPalette class
class ColorPalette {
  hue: number;
  complimentaryHue1: string | number;
  complimentaryHue2: string | number;
  saturation: number;
  lightness: number;
  baseColor: string;
  complimentaryColor1: string;
  complimentaryColor2: string;
  colorChoices: string[];

  constructor() {
    this.setColors();
    this.setCustomProperties();
  }

  setColors() {
    this.hue = ~~random(220, 360);
    this.complimentaryHue1 = this.hue + 30;
    this.complimentaryHue2 = this.hue + 60;
    this.saturation = 95;
    this.lightness = 50;

    this.baseColor = hsl(this.hue, this.saturation, this.lightness);
    this.complimentaryColor1 = hsl(
      this.complimentaryHue1,
      this.saturation,
      this.lightness
    );

    this.complimentaryColor2 = hsl(
      this.complimentaryHue2,
      this.saturation,
      this.lightness
    );

    this.colorChoices = [
      this.baseColor,
      this.complimentaryColor1,
      this.complimentaryColor2,
    ];
  }

  randomColor() {
    return this.colorChoices[~~random(0, this.colorChoices.length)].replace(
      '#',
      '0x'
    );
  }

  setCustomProperties() {
    //
  }
}

// Orb class
class Orb {
  bounds;
  scale: string | number;
  fill: string | number;
  radius: string | number;
  xOff: number;
  yOff: number;
  inc: number;
  x: number;
  y: number;
  graphics;

  // Pixi takes hex colors as hexidecimal literals (0x rather than a string with '#')
  constructor(fill = 0x000000, orbSpeed = 0.002) {
    // bounds = the area an orb is "allowed" to move within
    this.bounds = this.setBounds();

    // initialise the orb's { x, y } values to a random point within it's bounds
    this.x = random(this.bounds['x'].min, this.bounds['x'].max);
    this.y = random(this.bounds['y'].min, this.bounds['y'].max);

    // how large the orb is vs it's original radius (this will modulate over time)
    this.scale = 1;

    // what color is the orb?
    this.fill = fill;

    // the original radius of the orb, set relative to window height
    this.radius = random(window.innerHeight / 6, window.innerHeight / 3);

    // starting points in "time" for the noise/self similar random values
    this.xOff = random(0, 1000);
    this.yOff = random(0, 1000);

    // how quickly the noise/self similar random values step through time
    this.inc = orbSpeed;

    // PIXI.Graphics is used to draw 2d primitives (in this case a circle) to the canvas
    this.graphics = new PIXI.Graphics();
    this.graphics.alpha = 0.825;

    // 250ms after the last window resize event, recalculate orb positions.
    window.addEventListener(
      'resize',
      debounce(() => {
        this.bounds = this.setBounds();
      }, 250)
    );
  }

  setBounds() {
    // how far from the { x, y } origin can each orb move
    const maxDist =
      window.innerWidth < 1000 ? window.innerWidth / 3 : window.innerWidth / 5;
    // the { x, y } origin for each orb (the bottom right of the screen)
    const originX = window.innerWidth / 1.25;
    const originY =
      window.innerWidth < 1000
        ? window.innerHeight
        : window.innerHeight / 1.375;

    // allow each orb to move x distance away from it's x / y origin
    return {
      x: {
        min: originX - maxDist,
        max: originX + maxDist,
      },
      y: {
        min: originY - maxDist,
        max: originY + maxDist,
      },
    };
  }

  update() {
    // self similar "psuedo-random" or noise values at a given point in "time"
    const xNoise = simplex.noise2D(this.xOff, this.xOff); // simplex.noise2D
    const yNoise = simplex.noise2D(this.yOff, this.yOff); // simplex.noise2D
    const scaleNoise = simplex.noise2D(this.xOff, this.yOff); // simplex.noise2D

    // map the xNoise/yNoise values (between -1 and 1) to a point within the orb's bounds
    this.x = map(xNoise, -1, 1, this.bounds['x'].min, this.bounds['x'].max);
    this.y = map(yNoise, -1, 1, this.bounds['y'].min, this.bounds['y'].max);
    // map scaleNoise (between -1 and 1) to a scale value somewhere between half of the orb's original size, and 100% of it's original size
    this.scale = map(scaleNoise, -1, 1, 0.5, 1);

    // step through "time"
    this.xOff += this.inc;
    this.yOff += this.inc;
  }

  render() {
    // update the PIXI.Graphics position and scale values
    this.graphics.x = this.x;
    this.graphics.y = this.y;
    this.graphics.scale.set(this.scale);

    // clear anything currently drawn to graphics
    this.graphics.clear();

    // tell graphics to fill any shapes drawn after this with the orb's fill color
    this.graphics.beginFill(this.fill);
    // draw a circle at { 0, 0 } with it's size set by this.radius
    this.graphics.drawCircle(0, 0, this.radius);
    // let graphics know we won't be filling in any more shapes
    this.graphics.endFill();
  }
}

interface IOrbs {
  orbsFor: 'topic' | 'interaction';
  primaryOrbColor: string;
  secondaryOrbColor: string;
  tertiaryOrbColor: string;
  orbOpacity: number;
  orbBlurStrength: number;
  orbSpeed: number;
}

export const Orbs = (props: IOrbs) => {
  const {
    orbsFor = 'topic',
    primaryOrbColor = '#FD7EB8',
    secondaryOrbColor = '#B14FEC',
    tertiaryOrbColor = '#00d9d3',
    orbOpacity = 1,
    orbBlurStrength = 30,
    orbSpeed = 0.002,
  } = props;

  let calculatedOrbColors = tinycolor(primaryOrbColor).analogous();

  calculatedOrbColors = calculatedOrbColors.map(function(t) {
    return t.toHexString();
  });

  const pixiCanvas = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!pixiCanvas.current) {
      return;
    }

    // Create PixiJS app
    const app = new PIXI.Application({
      // render to <canvas class="orb-canvas"></canvas>
      view: pixiCanvas.current,
      //document.querySelector('.orb-canvas'),
      // auto adjust size to fit the current window
      resizeTo: window,
      // transparent background, we will be creating a gradient background later using CSS
      transparent: true,
    });

    app.stage.filters = [
      new (KawaseBlurFilter as any)(orbBlurStrength, 10, true),
    ];

    // Create colour palette
    const colorPalette = new ColorPalette();

    type TOrb = {
      graphics;
      update: () => void;
      render: () => void;
    };
    // Create orbs
    const orbs: TOrb[] = [];

    for (let i = 0; i < 10; i++) {
      const index = i % 3;
      const color = [
        Number(calculatedOrbColors[0].replace('#', '0x')),
        Number(calculatedOrbColors[1].replace('#', '0x')),
        Number(calculatedOrbColors[2].replace('#', '0x')),
      ][index]; // index

      const orb: TOrb = new Orb(color, orbSpeed);

      app.stage.addChild(orb.graphics);

      orbs.push(orb);
    }

    app.ticker.add(() => {
      orbs.forEach(orb => {
        orb.update();
        orb.render();
      });
    });
  }, [
    primaryOrbColor,
    secondaryOrbColor,
    tertiaryOrbColor,
    orbOpacity,
    orbBlurStrength,
    orbSpeed,
  ]);

  const { r, g, b } = hexToRGB(primaryOrbColor);

  return (
    <div
      style={{
        position: orbsFor === 'topic' ? 'fixed' : 'absolute',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 'inherit',
          top: 'inherit',
          width: 'inherit',
          height: 'inherit',
          background: `linear-gradient(to bottom, #fff, rgba(${r}, ${g},${b}, 0.6)`,
          opacity: orbOpacity,
        }}
      >
        <canvas
          ref={pixiCanvas}
          className="orb-canvas"
          style={{
            position: 'absolute',
            left: -20,
            top: -20,
            width: 'calc(100vw + 40px)',
            height: 'calc(100vh + 40px)',
            ...(!getWebGLEnabled() && {
              filter: `blur(${orbBlurStrength * 2}px)`,
            }),
          }}
        />
      </div>
    </div>
  );
};
